import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault } from '../lib/vault/vaultApi';
import {
  createGuardian,
  inviteGuardian,
  listGuardians,
  ROLE_COPY,
  type GuardianRole,
} from '../lib/guardians/guardianApi';
import './guardians.css';

const ROLES: GuardianRole[] = ['primary', 'backup', 'co_signer'];

export default function GuardianFormScreen() {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [vaultId, setVaultId] = useState<string | null>(null);
  const [taken, setTaken] = useState<Set<GuardianRole>>(new Set());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<GuardianRole>('primary');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const dash = await getMyVault(api);
        if (cancelled) return;
        setVaultId(dash.vault.id);
        const res = await listGuardians(api, dash.vault.id);
        if (cancelled) return;
        const used = new Set(res.guardians.filter((g) => g.status !== 'replaced').map((g) => g.role));
        setTaken(used);
        const free = ROLES.find((r) => !used.has(r));
        if (free !== undefined) setRole(free);
      } catch {
        if (!cancelled) setError('We could not open your vault just now.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (vaultId === null) return;
    if (name.trim() === '' || email.trim() === '') {
      setError('We need their name and their email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await createGuardian(api, vaultId, {
        invited_name: name.trim(),
        invited_email: email.trim(),
        role,
      });
      // Designating them does not tell them. Send the invitation now, so a
      // guardian who never hears about it can't quietly become a dead link in
      // the chain that only fails when it is finally needed.
      await inviteGuardian(api, vaultId, res.guardian.id);
      navigate('/guardians', { replace: true });
    } catch {
      setError('We could not add them. That role may already be taken.');
      setBusy(false);
    }
  }

  return (
    <section className="guard">
      <p className="guard__kicker">A person you trust</p>
      <h1 className="guard__title">Name a guardian.</h1>
      <p className="guard__sub">
        They will be asked to accept. Once they do, they hold one piece of your key — never the whole
        thing, and never anything they can read on their own.
      </p>

      <form onSubmit={(e) => void onSubmit(e)}>
        <label className="guard__field">
          <span className="guard__label">Their name</span>
          <input
            className="guard__input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </label>

        <label className="guard__field">
          <span className="guard__label">Their email</span>
          <input
            className="guard__input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>

        <fieldset className="guard__fieldset">
          <legend className="guard__label">Their part</legend>
          {ROLES.map((r) => {
            const disabled = taken.has(r) && r !== role;
            return (
              <label key={r} className={`guard__radio${disabled ? ' guard__radio--taken' : ''}`}>
                <input
                  type="radio"
                  name="role"
                  value={r}
                  checked={role === r}
                  disabled={disabled}
                  onChange={() => setRole(r)}
                />
                <span>
                  <strong>{ROLE_COPY[r].label}</strong>
                  {disabled && <em> — already named</em>}
                  <br />
                  <span className="guard__roleBlurb">{ROLE_COPY[r].blurb}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        {error !== null && (
          <p role="alert" className="guard__alert">
            {error}
          </p>
        )}

        <div className="guard__actions">
          <button type="button" className="la-btn-secondary" onClick={() => navigate('/guardians')}>
            Back
          </button>
          <div className="la-cta-wrap">
            <button type="submit" className="la-btn-primary" disabled={busy || vaultId === null}>
              {busy ? 'Sending' : 'Ask them'}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}
